import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #syncRequestId = 0;

  /** @type {number | undefined} */
  #pendingSyncTimeout;

  /** @type {number | undefined} */
  #pendingSyncTimeoutLate;

  /** @type {number} */
  get currentCartCount() {
    const value = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    return Number.isNaN(value) ? 0 : value;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(/** @type {string} */ (ThemeEvents.cartUpdate), this.onCartUpdate);
    document.addEventListener('click', this.onPotentialCartMutation);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(/** @type {string} */ (ThemeEvents.cartUpdate), this.onCartUpdate);
    document.removeEventListener('click', this.onPotentialCartMutation);
    window.removeEventListener('pageshow', this.onPageShow);
    if (this.#pendingSyncTimeout) {
      clearTimeout(this.#pendingSyncTimeout);
      this.#pendingSyncTimeout = undefined;
    }
    if (this.#pendingSyncTimeoutLate) {
      clearTimeout(this.#pendingSyncTimeoutLate);
      this.#pendingSyncTimeoutLate = undefined;
    }
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {Event} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const detail = /** @type {any} */ (event)?.detail || {};
    const itemCountFromResource = detail.resource?.item_count;
    const itemCountFromData = detail.data?.itemCount;
    const comingFromProductForm = detail.data?.source === 'product-form-component';

    // Prefer absolute count from cart resource to avoid drift from delta events.
    if (typeof itemCountFromResource === 'number') {
      this.renderCartBubble(itemCountFromResource, false);
      return;
    }

    // Fallback for events that only provide data.itemCount.
    if (typeof itemCountFromData === 'number') {
      this.renderCartBubble(itemCountFromData, comingFromProductForm);
      // Product form sends deltas; sync absolute value afterwards to avoid drift.
      if (comingFromProductForm) {
        void this.syncCartCountFromServer();
      }
      return;
    }

    // Some cart:update events are used as refresh signals and don't carry counts.
    // In that case, fetch the canonical cart count.
    void this.syncCartCountFromServer();
  };

  /**
   * Fallback: if a click is likely to mutate cart state, sync count from server shortly after.
   * @param {Event} event
   */
  onPotentialCartMutation = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(
      'add-to-cart-component, cart-items-component, quantity-selector-component, .quick-add__button, .add-to-cart-button'
    );

    if (!trigger) return;

    if (this.#pendingSyncTimeout) clearTimeout(this.#pendingSyncTimeout);
    if (this.#pendingSyncTimeoutLate) clearTimeout(this.#pendingSyncTimeoutLate);

    // First sync catches fast cart updates.
    this.#pendingSyncTimeout = setTimeout(() => {
      void this.syncCartCountFromServer();
    }, 350);

    // Second sync catches slower async flows (apps, warranty, bundled updates).
    this.#pendingSyncTimeoutLate = setTimeout(() => {
      void this.syncCartCountFromServer();
    }, 1300);
  };

  /**
   * Fetches canonical cart count from server and applies it.
   */
  syncCartCountFromServer = async () => {
    const requestId = ++this.#syncRequestId;

    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (requestId !== this.#syncRequestId) return;

      const itemCount = cart?.item_count;
      if (typeof itemCount !== 'number') return;

      this.renderCartBubble(itemCount, false, false);
    } catch (_) {
      // no-op
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
